import { gql } from 'graphql-request';

import {
  baseApi,
  select,
  insert,
  update,
  remove,
  afterInsert,
  afterUpdate,
  afterRemove,
  beforeUpdate,
  buildPaginationClauses,
  objectToStr,
  escapeBackslashes,
} from './base';

import {
  specProjectListType,
  specProjectType,
  specProjectParamType,
  specProjectProfileType,
  specProjectCalendarType,
} from '../graphqlTypes';

// updated getSpecProjects to handle search, archivedOnly, and starredOnly
const specProjectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSpecProjects: select(builder, 'spec_project', specProjectListType, false, {
      query: (params = {}) => {
        const archivedOnly = !!params.archivedOnly;
        const search = typeof params.search === 'string' ? params.search : '';
        const hasSearch = search.trim().length > 0;
        const starredOnly = !!params.starredOnly;
        const profileId =
          (typeof params.profileId === 'number' && Number.isFinite(params.profileId)) ||
          typeof params.profileId === 'string'
            ? params.profileId
            : undefined;

        // Build where _and conditions
        const andConditions = [`{ is_archived: { _eq: ${archivedOnly} } }`];

        if (hasSearch) {
          andConditions.push(
            `{ _or: [ { name: { _ilike: "${search}" } }, { spec_project_companies: { company: { name: { _ilike: "${search}" } } } } , { group: { profile_groups: { profile: { _or: [ { display_name: { _ilike: "${search}" } }, { email: { _ilike: "${search}" } } ] } } } } ] }`
          );
        }

        if (starredOnly && (typeof profileId === 'number' || typeof profileId === 'string')) {
          const profileIdLiteral = typeof profileId === 'number' ? profileId : `"${profileId}"`;
          andConditions.push(
            `{ spec_project_profiles: { is_star: { _eq: true }, profile_id: { _eq: ${profileIdLiteral} } } }`
          );
        }

        const where = `where: { _and: [ ${andConditions.join(', ')} ] }`;

        const order = 'order_by: { updated_at: desc }';

        const clauses = [where, order];
        const [limitClause, offsetClause] = buildPaginationClauses({
          params,
          hasSearch,
        });
        clauses.push(limitClause);
        clauses.push(offsetClause);

        const finalClauses = clauses.join(', ');

        return {
          body: gql`
            query {
              spec_project(${finalClauses}) ${specProjectListType}
            }
          `,
        };
      },
    }),
    getSpecProject: select(builder, 'spec_project', specProjectType, true),
    insertSpecProject: builder.mutation({
      query: (project) => {
        project = escapeBackslashes(project);
        return {
          body: gql`
          mutation {
            insert_spec_project_one(object: {
              code: "${project.code}", name: "${project.name}",
              state: "${project.state}", region: "${project.region}", green_star: ${
            project.greenStar
          },
              description: "${project.description}", procurement_type: "${project.procurementType}",
              address: "${project.address}", client: "${project.client}", notes: "${project.notes}",
              cost: ${project.constructionCost}, gfa: ${project.gfa}, numbering: ${objectToStr(
            project.numbering
          )},
              image_url: "${project.image || ''}",
              ${
                project.sectors.length > 0
                  ? `spec_project_sectors: {data: ${objectToStr(
                      project.sectors.map((s) => ({ name: s }))
                    )}},`
                  : ''
              }
              ${
                project.typesOfWork.length > 0
                  ? `spec_project_work_types: {data: ${objectToStr(
                      project.typesOfWork.map((t) => ({ name: t }))
                    )}},`
                  : ''
              }
              spec_project_companies: {data: ${objectToStr(
                project.architects.map((a) => ({ company_id: a.id, branch_name: a.branch_name || null }))
              )}}
            })  ${specProjectType}
          }
          `,
        };
      },
      transformResponse: (response) => response.insert_spec_project_one,
      onQueryStarted: afterInsert('getSpecProjects'),
    }),
    updateSpecProject: update(
      builder,
      'spec_project',
      specProjectType,
      beforeUpdate('getSpecProjects')
    ),
    updateSpecProjectWithDependencies: builder.mutation({
      query: (project) => {
        project = escapeBackslashes(project);
        return {
          body: gql`
          mutation {
            delete_spec_project_sector(where: {spec_project_id: {_eq: ${project.id}}})
            {affected_rows}
            insert_spec_project_sector(
              objects: ${objectToStr(
                project.sectors.map((s) => ({ name: s, spec_project_id: project.id }))
              )}, 
            )  {affected_rows}
            delete_spec_project_work_type(where: {spec_project_id: {_eq: ${project.id}}})
            {affected_rows}
            insert_spec_project_work_type(
              objects: ${objectToStr(
                project.typesOfWork.map((s) => ({ name: s, spec_project_id: project.id }))
              )}, 
            )  {affected_rows}
            delete_spec_project_company(where: {spec_project_id: {_eq: ${project.id}}})
            {affected_rows}
            insert_spec_project_company(
              objects: ${objectToStr(
                project.architects.map((a) => ({ spec_project_id: project.id, company_id: a.id, branch_name: a.branch_name || null }))
              )},
            )  {affected_rows}
            update_spec_project_by_pk(pk_columns: {id: ${project.id}}, _set: {
              code: "${project.code}", name: "${project.name}", region: "${project.region}",
              state: "${project.state}", green_star: ${project.greenStar}, revit: "${
            project.revit
          }",
              description: "${project.description}", procurement_type: "${project.procurementType}",
              address: "${project.address}", client: "${project.client}", notes: "${
            project.notes
          }", owner: "${project.owner}",
              cost: ${project.constructionCost}, gfa: ${project.gfa}, numbering: ${objectToStr(
            project.numbering
          )},
              is_archived: ${project.isArchived}, nda: "${project.nda}",
              image_url: "${project.image || ''}"
            })  ${specProjectType}
          }
          `,
        };
      },
      transformResponse: (response) => response.update_spec_project_by_pk,
      invalidatesTags: (result, error, arg) => [
        { type: 'SpecProject', id: arg.id.toString() },
        'Sheet',
      ],
      onQueryStarted: afterUpdate('getSpecProjects'),
    }),
    getSpecProjectParams: select(builder, 'spec_project_param', specProjectParamType, false, {
      filter: (params) => `{spec_project_id: {_eq: ${params.spec_project_id}}}`,
      order: `{key: asc}`,
    }),
    updateSpecProjectParam: update(
      builder,
      'spec_project_param',
      specProjectParamType,
      afterUpdate('getSpecProjectParams'),
      ['Sheet']
    ),
    insertSpecProjectParam: insert(
      builder,
      'spec_project_param',
      specProjectParamType,
      afterInsert('getSpecProjectParams'),
      ['Sheet']
    ),
    removeSpecProjectParam: remove(
      builder,
      'spec_project_param',
      specProjectParamType,
      afterRemove('getSpecProjectParams'),
      ['Sheet']
    ),
    insertSpecProjectProfile: insert(
      builder,
      'spec_project_profile',
      specProjectProfileType,
      undefined,
      ['SpecProject']
    ),
    updateSpecProjectProfile: update(
      builder,
      'spec_project_profile',
      specProjectProfileType,
      undefined,
      ['SpecProject']
    ),
    getSpecProjectCalendars: select(builder, 'spec_project_calendar', specProjectCalendarType),
    insertSpecProjectCalendar: insert(builder, 'spec_project_calendar', specProjectCalendarType),
    updateSpecProjectCalendar: update(builder, 'spec_project_calendar', specProjectCalendarType),
    deleteSpecProjectCalendar: remove(builder, 'spec_project_calendar', specProjectCalendarType),
  }),
  overrideExisting: false,
});

export const {
  useGetSpecProjectsQuery,
  useGetSpecProjectQuery,
  useInsertSpecProjectMutation,
  useUpdateSpecProjectMutation,
  useUpdateSpecProjectWithDependenciesMutation,
  useGetSpecProjectParamsQuery,
  useUpdateSpecProjectParamMutation,
  useInsertSpecProjectParamMutation,
  useRemoveSpecProjectParamMutation,
  useInsertSpecProjectProfileMutation,
  useUpdateSpecProjectProfileMutation,
  useInsertSpecProjectCalendarMutation,
  useUpdateSpecProjectCalendarMutation,
  useDeleteSpecProjectCalendarMutation,
  useGetSpecProjectCalendarsQuery,
} = specProjectApi;
